import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { gql, useQuery } from '@apollo/client';
import { Alert, Skeleton, Stack, Typography } from '@mui/material';
import { FormInputSelect } from 'components/form';
import { SelectOption } from 'global';

const QUERY_VENDOR_PROFILES_ASSOCIATED_TO_LENDER = gql`
  query vendorProfilesByDynamicsLenderAccountId($dynamicsAccountId: ID!) {
    vendorProfilesByDynamicsLenderAccountId(dynamicsAccountId: $dynamicsAccountId) {
      id
      dynamicsId
      name
    }
  }
`;

interface VendorProfileOption {
  id: string;
  dynamicsId: string;
  name: string;
}

interface Props {
  value: string;
  onChange: (dynamicsId: string) => void;
}

// Shown when review_reasons includes DEALER_NOT_MATCHED — the workflow couldn't confidently
// resolve the dealer named in the document, so a human has to pick one before this can be confirmed.
export const DealerResolutionField = ({ value, onChange }: Props) => {
  const account = useSelector((state: any) => state.account);

  const { data, loading, error } = useQuery<{ vendorProfilesByDynamicsLenderAccountId: VendorProfileOption[] }>(
    QUERY_VENDOR_PROFILES_ASSOCIATED_TO_LENDER,
    {
      skip: !account?.dynamicsAccountId,
      variables: { dynamicsAccountId: account?.dynamicsAccountId },
    }
  );

  const dealerOptions: SelectOption[] = useMemo(
    () =>
      (data?.vendorProfilesByDynamicsLenderAccountId ?? []).map(vp => ({
        label: vp.name.replace('VP - ', ''),
        value: vp.dynamicsId,
      })),
    [data]
  );

  return (
    <Alert severity="warning" icon={false}>
      <Stack spacing={1}>
        <Typography variant="body2">
          We couldn't confidently match this application to one of your connected dealers. Select the correct dealer
          before confirming.
        </Typography>
        {loading ? (
          <Skeleton variant="rounded" height={56} />
        ) : error ? (
          <Typography variant="body2" color="error">
            Couldn't load your connected dealers. Please refresh the page and try again.
          </Typography>
        ) : dealerOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No connected dealers were found for your account.
          </Typography>
        ) : (
          <FormInputSelect
            size="small"
            label="Dealer"
            variant="standard"
            value={value}
            options={dealerOptions}
            onChange={onChange}
          />
        )}
      </Stack>
    </Alert>
  );
};
